// src/components/SellOfferEdit/SellOfferEdit.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import api from "../Axios/axios";
import "./SellOfferEdit.scss";

const DEFAULT_SALES_MACHINE_SPECS = [
  { label: "Ürün", value: "" },
  { label: "Marka ve Model", value: "" },
  { label: "İmal Yılı", value: "" },
  { label: "Mast Boyut", value: "" },
  { label: "Mast Adet", value: "" },
  { label: "Max Kaldırma Kapasite", value: "" },
  { label: "Bom Uzunluğu", value: "" },
  { label: "Bom Ucu Kapasite", value: "" },
  { label: "Serbest Duruş Yükseklik", value: "" },
  { label: "Kaldırma", value: "" },
  { label: "Çevirme", value: "" },
  { label: "Sürüş", value: "" },
  { label: "Zemin Tipi", value: "" },
  { label: "Menşei", value: "" },
];

const normalizeSalesMachineSpecs = (rows = [], selectedMachineName = "") => {
  const read = (...labels) => {
    const found = rows.find((row) =>
      labels.some((label) => (row.label || "").trim().toLowerCase() === label.toLowerCase())
    );
    return found?.value || "";
  };

  return DEFAULT_SALES_MACHINE_SPECS.map((spec) => {
    let value = read(spec.label);

    if (!value && spec.label === "Ürün") value = selectedMachineName || read("Makine", "Ürün");
    if (!value && spec.label === "Marka ve Model") {
      value = [read("Marka"), read("Model")].filter(Boolean).join(" ").trim() || read("Marka ve Model");
    }
    if (!value && spec.label === "İmal Yılı") value = read("Model Yılı", "İmal Yılı");
    if (!value && spec.label === "Max Kaldırma Kapasite") value = read("Maks. Kapasite", "Max Kaldırma Kapasite");
    if (!value && spec.label === "Serbest Duruş Yükseklik") value = read("Serbest Yükseklik (Kanca Altı)", "Serbest Duruş Yükseklik");
    if (!value && spec.label === "Menşei") value = read("Menşe", "Menşei");

    return { ...spec, value };
  });
};


export default function SellOfferEdit() {
    const { id } = useParams();
    const navigate = useNavigate();

    // --- Firms ---
    const [allCompanies, setAllCompanies] = useState([]);
    const [sellerCompanies, setSellerCompanies] = useState([]);
    const [sellerId, setSellerId] = useState("");
    const [buyerId, setBuyerId] = useState("");

    const [sellerInfo, setSellerInfo] = useState({
        taxNo: "", address: "", contact: "", phone: "", email: ""
    });

    const [buyerInfo, setBuyerInfo] = useState({
        companyName: "", taxOffice: "", taxNo: "", address: "", contact: "", phone: "", email: ""
    });

    // --- Teklif türü ---
    const [offerType, setOfferType] = useState("SalesOffer");

    // --- Makine şablonu ---
    const [machineOptions, setMachineOptions] = useState([]);
    const [selectedMachineId, setSelectedMachineId] = useState("");
    const [selectedMachineName, setSelectedMachineName] = useState("");

    // --- Dinamik makine satırları ---
    const [machineSpecs, setMachineSpecs] = useState(DEFAULT_SALES_MACHINE_SPECS);

    // --- Fiyat ---
    const [pricing, setPricing] = useState({ currency: "USD", quantity: 1, price: "" });

    // --- Açıklamalar ---
    const [details, setDetails] = useState([
        "•Gümrük Tarife Kodu: 8426200000",
        "•Üretim Standardı: Kule vinç, CE standartlarına uygun olarak üretilmiştir.",
        "•Sevkiyat Süresi: Ödeme sonrası 15 gün içinde ilk gemiye rezervasyon yapılması garanti edilmektedir. Tahmini gemi geliş süresi 45-60 gündür.",
        "•Teslim Şekli: CIF Gümrük Teslim",
        "•Ödeme Şartı: %100 Peşin Ödeme.",
        "•Çalışması için zorunlu olanlar dışında aksam ve aksesuarlar bulunmamaktadır.",
        "•Proforma Geçerlilik Süresi: 7 gündür (Nakliye sebebiyle haftalık fiyat kontrol edilmesi gerekmektedir.)",
    ]);

    // ---- İlk veriler ----
    useEffect(() => {
        api.get("/Company")
            .then((res) => setSellerCompanies((res?.data?.data || []).filter((c) => c.hasTowerCrane)))
            .catch(() => setSellerCompanies([]));

        api.get("/Company")
            .then((res) => setAllCompanies(res?.data?.data || []))
            .catch(() => setAllCompanies([]));

        api.get("/Machine")
            .then((res) => setMachineOptions(res?.data?.data || []))
            .catch(() => setMachineOptions([]));
    }, []);

    // ---- Mevcut teklifi yükle ----
    useEffect(() => {
        if (!id) return;
        api.get(`/SalesOffer/${id}`)
            .then((res) => {
                const data = res.data.data || res.data;
                if (!data) return;

                setOfferType(data.type === "SalesLeasingOffer" ? "SalesLeasingOffer" : "SalesOffer");

                // Satıcı ID
                setSellerId(data.rentingCompanyId ? String(data.rentingCompanyId) : "");

                // Makine adı
                setSelectedMachineName(data.machine || "");

                // Fiyat
                setPricing({
                    currency: data.currency || "USD",
                    quantity: data.piece || 1,
                    price: data.price ? String(data.price) : ""
                });

                // Alıcı bilgileri
                setBuyerInfo({
                    companyName: data.buyerCompanyName || data.buyerCompany || "",
                    taxOffice: data.buyerCompanyTaxOffice || "",
                    taxNo: data.buyerCompanyTaxNo || "",
                    address: data.buyerCompanyAddress || "",
                    contact: data.buyerCompanyAuthorizedPerson || "",
                    phone: data.buyerCompanyAuthorizedPersonPhone || "",
                    email: data.buyerCompanyAuthorizedPersonEmail || ""
                });

                // Makine özellikleri
                const specs = (data.machineTemplates || []).map(t => ({
                    label: t.title || "",
                    value: t.description || ""
                }));
                if (specs.length) setMachineSpecs(specs);

                // Açıklamalar
                const descs = (data.descriptions || []).map(d => d.description || "");
                if (descs.length) setDetails(descs);

            })
            .catch((err) => console.error("Teklif yüklenemedi", err));
    }, [id]);

    // ---- Teklif türüne göre açıklamaları getir ----
    useEffect(() => {
        if (!id) return; // Sadece yeni oluştururken çalışsın, edit'te mevcut data korunsun
        api.get("/Description", { params: { type: offerType } })
            .then((res) => {
                const arr = (res?.data?.data || [])
                    .map((d) => d.name || d.description)
                    .filter(Boolean);
                if (arr.length) setDetails(arr);
            })
            .catch(() => { });
    }, [offerType, id]);

    // ---- Firma seçilirse bilgilerini doldur ----
    useEffect(() => {
        if (!sellerId) return;
        api.get(`/Company/GetById?id=${sellerId}`).then((res) => {
            const d = res?.data?.data || {};
            setSellerInfo({
                taxNo: d.taxNo || "",
                address: d.address || "",
                contact: d.authorizedPerson1 || "",
                phone: d.phoneNumber1 || "",
                email: d.email || "",
            });
        });
    }, [sellerId]);

    useEffect(() => {
        if (!buyerId) return;
        api.get(`/Company/GetById?id=${buyerId}`).then((res) => {
            const d = res?.data?.data || {};
            const [taxOffice, taxNo] = (d.taxNo || "").split("/") || ["", ""];
            setBuyerInfo({
                companyName: d.name || "",
                taxOffice: taxOffice || "",
                taxNo: taxNo || "",
                address: d.address || "",
                contact: d.authorizedPerson1 || "",
                phone: d.phoneNumber1 || "",
                email: d.email || ""
            });
        });
    }, [buyerId]);

    // ---- Makine seçimi ----
    useEffect(() => {
        if (!selectedMachineId) {
            setSelectedMachineName("");
        } else {
            api.get(`/Machine/${selectedMachineId}`)
                .then((res) => {
                    const d = res?.data?.data;
                    if (!d) return;
                    setSelectedMachineName(d.model || "");
                    const rows = (d.machineTemplates || []).map((t) => ({
                        label: (t.title || "").trim().replace(/\s*:\s*$/, ""),
                        value: t.description || "",
                    }));
                    if (rows.length && window.confirm("Mevcut makine özelliklerini seçilen şablonla değiştirmek istiyor musunuz?")) {
                        setMachineSpecs(normalizeSalesMachineSpecs(rows, d.model || ""));
                    }
                })
                .catch(() => { });
        }
    }, [selectedMachineId]);

    // ---- Helpers ----
    const priceNumber = useMemo(
        () => Number(String(pricing.price).replace(/\./g, "").replace(/,/g, ".")) || 0,
        [pricing.price]
    );
    const quantityNumber = Number(pricing.quantity || 0);

    const addSpecRow = () => setMachineSpecs((arr) => [...arr, { label: "", value: "" }]);
    const removeSpecRow = (idx) => setMachineSpecs((arr) => arr.filter((_, i) => i !== idx));

    const getSpecVal = (label) =>
        machineSpecs.find(s => (s.label || "").toLowerCase() === label.toLowerCase())?.value?.trim() || "";

    const buildMachineString = () => {
        if (selectedMachineName) return selectedMachineName;
        const brand = getSpecVal("Marka");
        const model = getSpecVal("Model");
        const name = [brand, model].filter(Boolean).join(" ").trim();
        return name || "Makine";
    };

    // ---- Güncelle: PUT endpoint ----
    const updateOffer = () => {
        if (!quantityNumber || quantityNumber < 1) { alert("Adet giriniz!"); return; }
        if (!priceNumber || priceNumber <= 0) { alert("Fiyat giriniz!"); return; }

        const payload = {
            id: Number(id),

            // Satıcı firma ID (opsiyonel)
            ...(sellerId ? { rentingCompanyId: sellerId } : {}),

            // Alıcı bilgileri
            buyerCompany: buyerInfo.companyName,
            buyerCompanyTaxOffice: buyerInfo.taxOffice,
            buyerCompanyTaxNo: buyerInfo.taxNo,
            buyerCompanyName: buyerInfo.companyName,
            buyerCompanyAddress: buyerInfo.address,
            buyerCompanyAuthorizedPerson: buyerInfo.contact,
            buyerCompanyAuthorizedPersonPhone: buyerInfo.phone,
            buyerCompanyAuthorizedPersonEmail: buyerInfo.email,

            machine: buildMachineString(),

            machineTemplates: machineSpecs.map(s => ({
                title: s.label || "",
                description: s.value || ""
            })),

            currency: pricing.currency,
            piece: quantityNumber,
            price: priceNumber,

            descriptions: details.map(d => ({ description: d }))
        };

        console.log("PUT Payload:", payload);

        api.put("/SalesOffer", payload)
            .then(() => {
                alert("Satış teklifi başarıyla güncellendi!");
                navigate("/offerlist");
            })
            .catch((err) => {
                console.error("Güncelleme hatası:", err);
                alert("Güncelleme sırasında hata oluştu.");
            });
    };

    return (
        <>
            <Navbar />
            <div className="sell-offer-edit">
                <h2>Satış Teklifi Düzenle</h2>

                <div className="offer-form">

                    {/* Teklifi Veren (opsiyonel seçim) */}
                    <div className="form-row">
                        <label>Teklifi Veren (Satıcı) Firma</label>
                        <select value={sellerId} onChange={(e) => setSellerId(e.target.value)}>
                            <option value="">Satıcı Firma</option>
                            {sellerCompanies.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Alıcı (opsiyonel seçim) */}
                    <div className="form-row">
                        <label>Alıcı Firma (Teklif Verilen)</label>
                        <select value={buyerId} onChange={(e) => setBuyerId(e.target.value)}>
                            <option value="">Alıcı Firma</option>
                            {allCompanies.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-row multi">
                        <div>
                            <label>Firma Ünvanı</label>
                            <input value={buyerInfo.companyName} onChange={(e) => setBuyerInfo({ ...buyerInfo, companyName: e.target.value })} />
                        </div>
                        <div>
                            <label>Vergi Dairesi</label>
                            <input value={buyerInfo.taxOffice} onChange={(e) => setBuyerInfo({ ...buyerInfo, taxOffice: e.target.value })} />
                        </div>
                        <div>
                            <label>Vergi No</label>
                            <input value={buyerInfo.taxNo} onChange={(e) => setBuyerInfo({ ...buyerInfo, taxNo: e.target.value })} />
                        </div>
                        <div>
                            <label>Adres</label>
                            <input value={buyerInfo.address} onChange={(e) => setBuyerInfo({ ...buyerInfo, address: e.target.value })} />
                        </div>
                        <div>
                            <label>Yetkili</label>
                            <input value={buyerInfo.contact} onChange={(e) => setBuyerInfo({ ...buyerInfo, contact: e.target.value })} />
                        </div>
                        <div>
                            <label>Tel</label>
                            <input value={buyerInfo.phone} onChange={(e) => setBuyerInfo({ ...buyerInfo, phone: e.target.value })} />
                        </div>
                        <div>
                            <label>Email</label>
                            <input value={buyerInfo.email} onChange={(e) => setBuyerInfo({ ...buyerInfo, email: e.target.value })} />
                        </div>
                    </div>

                    {/* Makine Şablonu (opsiyonel) */}
                    <div className="form-row">
                        <label>Makine Şablonu (Opsiyonel)</label>
                        <select
                            value={selectedMachineId}
                            onChange={(e) => setSelectedMachineId(e.target.value)}
                        >
                            <option value="">Seçin…</option>
                            {machineOptions.map((m) => (
                                <option key={m.id} value={m.id}>{m.model}</option>
                            ))}
                        </select>
                    </div>

                    {/* Dinamik Makine Bilgileri */}
                    <h3>Makine Bilgileri</h3>
                    <div className="specs-table">
                        <div className="specs-head">
                            <div>Başlık</div>
                            <div>Açıklama</div>
                            <div className="specs-actions-col">Sil</div>
                        </div>
                        {machineSpecs.map((row, idx) => (
                            <div className="specs-row" key={idx}>
                                <input
                                    className="spec-input"
                                    placeholder="Örn: Marka"
                                    value={row.label}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setMachineSpecs((arr) => {
                                            const copy = [...arr];
                                            copy[idx] = { ...copy[idx], label: v };
                                            return copy;
                                        });
                                    }}
                                />
                                <input
                                    className="spec-input"
                                    placeholder="Örn: Turkish Tower Cranes"
                                    value={row.value}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setMachineSpecs((arr) => {
                                            const copy = [...arr];
                                            copy[idx] = { ...copy[idx], value: v };
                                            return copy;
                                        });
                                    }}
                                />
                                <button
                                    type="button"
                                    className="remove-btn"
                                    onClick={() => removeSpecRow(idx)}
                                    title="Satırı sil"
                                >
                                    —
                                </button>
                            </div>
                        ))}
                        <button type="button" className="btn-small" onClick={addSpecRow}>
                            + Satır Ekle
                        </button>
                    </div>

                    {/* Fiyat */}
                    <h3>Fiyat</h3>
                    <div className="form-row multi">
                        <div>
                            <label>Para Birimi</label>
                            <select
                                value={pricing.currency}
                                onChange={(e) => setPricing({ ...pricing, currency: e.target.value })}
                            >
                                <option>USD</option>
                                <option>EUR</option>
                                <option>TRY</option>
                            </select>
                        </div>
                        <div>
                            <label>Adet</label>
                            <input
                                type="number"
                                min="1"
                                value={pricing.quantity}
                                onChange={(e) => setPricing({ ...pricing, quantity: Number(e.target.value) })}
                                placeholder="Örn: 1"
                            />
                        </div>
                        <div>
                            <label>Fiyat</label>
                            <input
                                value={pricing.price}
                                onChange={(e) => setPricing({ ...pricing, price: e.target.value })}
                                placeholder="Örn: 250000"
                            />
                        </div>
                    </div>

                    {/* Teklif Türü */}
                    <div className="form-row">
                        <label>Teklif Türü</label>
                        <select value={offerType} onChange={(e) => setOfferType(e.target.value)}>
                            <option value="SalesOffer">Peşin</option>
                            <option value="SalesLeasingOffer">Leasing</option>
                        </select>
                    </div>

                    {/* Açıklamalar */}
                    <h3>Detay Açıklamaları</h3>
                    {details.map((d, i) => (
                        <div className="form-row details-container" key={i}>
                            <input
                                className="detail-input"
                                value={d}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setDetails((arr) => {
                                        const c = [...arr];
                                        c[i] = v;
                                        return c;
                                    });
                                }}
                            />
                            <button
                                type="button"
                                className="remove-btn"
                                onClick={() => setDetails((arr) => arr.filter((_, idx) => idx !== i))}
                            >
                                —
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        className="btn-small"
                        onClick={() => setDetails((d) => [...d, ""])}
                    >
                        + Detay Ekle
                    </button>

                    <div className="actions">
                        <button className="btn-secondary" onClick={() => navigate("/offerlist")}>İptal</button>
                        <button className="btn-primary" onClick={updateOffer}>Güncelle</button>
                    </div>
                </div>

                {/* Özet */}
                <div className="summary">
                    <h4>Özet</h4>
                    <ul>
                        <li><strong>Tür:</strong> {offerType === "SalesOffer" ? "Satış" : "Leasing"}</li>
                        <li>
                            <strong>Satıcı:</strong>{" "}
                            {sellerId
                                ? (sellerCompanies.find((c) => c.id === sellerId)?.name || "-")
                                : (sellerInfo.contact || sellerInfo.email || "Elle")}
                        </li>
                        <li>
                            <strong>Alıcı:</strong>{" "}
                            {buyerId
                                ? (allCompanies.find((c) => c.id === buyerId)?.name || "-")
                                : (buyerInfo.companyName || "Elle")}
                        </li>
                        <li><strong>Adet / Fiyat:</strong> {pricing.quantity} adet • {priceNumber.toLocaleString("tr-TR")} {pricing.currency}</li>
                        <li><strong>Makine:</strong> {buildMachineString()}</li>
                        <li><strong>Makine Satırları:</strong> {machineSpecs.length} adet</li>
                    </ul>
                </div>
            </div>
        </>
    );
}
